import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import {
  authLogin,
  authLogout,
  authMe,
  authRegister,
  type AuthUser,
} from '../lib/api';
import { clearStoredAuthToken, getStoredAuthToken, setStoredAuthToken } from '../lib/authToken';

type AuthState = {
  user: AuthUser | null;
  token: string | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<{ ok: true } | { ok: false; message: string }>;
  register: (
    email: string,
    password: string,
    signupSource?: string | null
  ) => Promise<{ ok: true } | { ok: false; message: string }>;
  logout: () => Promise<void>;
  refreshMe: () => Promise<void>;
};

const AuthContext = createContext<AuthState | null>(null);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [token, setToken] = useState<string | null>(() => getStoredAuthToken());
  const [loading, setLoading] = useState(true);

  const refreshMe = useCallback(async () => {
    const t = getStoredAuthToken();
    if (!t) {
      setUser(null);
      setToken(null);
      setLoading(false);
      return;
    }
    const r = await authMe(t);
    if (r.ok) {
      setUser(r.user);
      setToken(t);
    } else {
      clearStoredAuthToken();
      setUser(null);
      setToken(null);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    void refreshMe();
  }, [refreshMe]);

  const login = useCallback(async (email: string, password: string) => {
    const r = await authLogin(email, password);
    if (!r.ok) {
      return { ok: false as const, message: r.message ?? r.error };
    }
    setStoredAuthToken(r.token);
    setToken(r.token);
    setUser(r.user);
    return { ok: true as const };
  }, []);

  const register = useCallback(async (email: string, password: string, signupSource?: string | null) => {
    const r = await authRegister(email, password, signupSource);
    if (!r.ok) {
      return { ok: false as const, message: r.message ?? r.error };
    }
    setStoredAuthToken(r.token);
    setToken(r.token);
    setUser(r.user);
    return { ok: true as const };
  }, []);

  const logout = useCallback(async () => {
    const t = getStoredAuthToken();
    if (t) await authLogout(t);
    clearStoredAuthToken();
    setUser(null);
    setToken(null);
  }, []);

  const value = useMemo(
    () => ({
      user,
      token,
      loading,
      login,
      register,
      logout,
      refreshMe,
    }),
    [user, token, loading, login, register, logout, refreshMe]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthState {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth outside AuthProvider');
  return ctx;
}
